import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useCallback, useEffect, useState } from 'react';
import { Pressable } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { SummaryThumbs } from '@/components/SummaryThumbs';
import { formatCaseLineForDisplay } from '@/components/chat/bubbles/caseSummaryState';
import { SUPPORT_REASON_INQUIRY_SUMMARY_FEEDBACK, SupportForm } from '@/components/supportForm';
import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import { useSupportPrompt } from '@/features/support-contact';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { isRecommendedCaseLayout, useCaseLayout } from '@/services/chat/caseLayoutSwitch';
import {
  notifyCaseBoxLayout,
  registerCaseBox,
  useClinicalCaseHeaderStore,
} from '@/services/chat/clinicalCaseHeader';
import type { ClinicalCase } from '@/types/clinicalCase';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';

/**
 * Keeps the box's view on native so the chat list can measure it. Web draws a plain div, which
 * has no such flattening and would print the prop as an invalid DOM attribute.
 */
const KEEP_VIEW_PROPS = isWeb ? {} : { collapsable: false };

type ClinicalCaseBlockProps = {
  clinicalCase: ClinicalCase | undefined;
  conversationId: string;
  /** Which side the bubble sits on, so the recommended row picks its pill and text colors. */
  position?: 'left' | 'right';
};

/**
 * A quiet box with no border or icon, so the generated summary does not outrank the message it
 * summarizes. The label stays plain and the footer carries the AI attribution.
 */
const CASE_SUMMARY_BOX_CLASS_NAME = 'w-full gap-1 rounded-md bg-background-0 p-3';

/**
 * The submitter's case summary under their opening message, first in the "Summarized by Primum"
 * section that also holds the extracted questions. The message stays unchanged above it. The
 * section caption says who wrote it, so the footer carries only the feedback thumbs.
 *
 * `preparing` reserves one muted line so the message does not jump when the case lands.
 * `refused`, `none` and an absent field render nothing.
 */
export function ClinicalCaseBlock({
  clinicalCase,
  conversationId,
  position = 'left',
}: ClinicalCaseBlockProps) {
  const layout = useCaseLayout();

  /** The chat list measures this view to know whether the box is on screen. */
  const boxRef = useCallback(
    (node: Parameters<typeof registerCaseBox>[1]) => registerCaseBox(conversationId, node),
    [conversationId]
  );

  const handleLayout = useCallback(() => notifyCaseBoxLayout(conversationId), [conversationId]);

  if (isRecommendedCaseLayout(layout)) {
    return <RecommendedCaseRow clinicalCase={clinicalCase} />;
  }

  if (clinicalCase?.status === 'written' && clinicalCase.line.trim()) {
    return (
      <Box
        ref={boxRef}
        {...KEEP_VIEW_PROPS}
        onLayout={handleLayout}
        className="pt-2"
        testID="clinical-case-block">
        <VStack className={CASE_SUMMARY_BOX_CLASS_NAME}>
          <Text size="sm" className="font-body font-semibold text-typography-900">
            Case summary
          </Text>
          <Text size="sm" className="font-body leading-relaxed text-typography-800">
            {clinicalCase.line}
          </Text>
          <CaseFooter conversationId={conversationId} source="message" showAttribution={false} />
        </VStack>
      </Box>
    );
  }

  if (clinicalCase?.status === 'preparing') {
    return (
      <Box
        ref={boxRef}
        {...KEEP_VIEW_PROPS}
        onLayout={handleLayout}
        className="pt-2"
        testID="clinical-case-block">
        <VStack
          className={CASE_SUMMARY_BOX_CLASS_NAME}
          accessibilityRole="text"
          accessibilityLiveRegion="polite"
          role="status">
          <Text size="sm" className="font-body text-typography-700">
            Preparing case summary…
          </Text>
        </VStack>
      </Box>
    );
  }

  return null;
}

const FOLLOW_UP_HIT_SLOP = getAccessibleHitSlop(16);

const THUMB_LABELS = {
  up: 'This case summary was helpful',
  down: 'This case summary was not helpful',
} as const;

/**
 * The attribution and the feedback signal under a case summary, the same pattern as the Inquiry
 * Library summary card, through the same `SummaryThumbs`: thumbs beside "Summarized by Primum AI", and after a thumbs down a link
 * to the support form so the reader can say what was wrong. Pressing the chosen thumb again
 * takes the vote back, reported as `cleared`. The vote is shared with the peek, so both copies
 * agree. The event carries ids and the vote only, never the line, which describes a patient.
 *
 * `onGoToMessage` adds a way back to the opening message, for the peek that shows the summary
 * away from it. `showAttribution` is false inside the message, where the section caption above the
 * box already says Primum wrote it.
 */
export function CaseFooter({
  conversationId,
  source,
  onGoToMessage,
  showAttribution = true,
  part = 'all',
}: {
  conversationId: string;
  source: 'message' | 'peek';
  onGoToMessage?: () => void;
  showAttribution?: boolean;
  /**
   * The recommended layout splits the footer: `thumbs` is the thumbs with their "How did we do?" label at the end of the
   * case row, `followUp` is the thanks and support link under the line. `all` is the whole footer.
   */
  part?: 'all' | 'thumbs' | 'followUp';
}) {
  const { currentUser } = useAuthStatus();

  const { track } = useChatAnalytics();

  const supportPrompt = useSupportPrompt({
    source: 'clinical_case',
    draft: `About the case summary on conversation ${conversationId}: `,
  });

  const vote = useClinicalCaseHeaderStore((state) => state.votes[conversationId]);

  const setVote = useClinicalCaseHeaderStore((state) => state.setVote);

  const rate = (pressed: 'up' | 'down') => {
    const next = vote === pressed ? null : pressed;

    setVote(conversationId, next);

    track(ANALYTICS_EVENTS.CLINICAL_CASE_RATED, { rating: next ?? 'cleared', source });
  };

  if (part === 'thumbs') {
    return (
      <SummaryThumbs vote={vote} onRate={rate} labels={THUMB_LABELS} textClassName="text-xs" />
    );
  }

  return (
    <>
      {part === 'all' ? (
        <HStack
          className={twJoin(
            'flex-wrap items-center gap-x-3 gap-y-1',
            showAttribution ? 'justify-between' : 'justify-end'
          )}>
          {showAttribution ? (
            <Text size="xs" className="text-typography-700">
              Summarized by Primum AI
            </Text>
          ) : null}

          <SummaryThumbs vote={vote} onRate={rate} labels={THUMB_LABELS} textClassName="text-xs" />
        </HStack>
      ) : null}

      {vote || onGoToMessage ? (
        <HStack className="flex-wrap items-center gap-1">
          {vote ? (
            <Text size="xs" className="text-typography-800">
              Thanks!
            </Text>
          ) : null}
          {/* A Pressable, not a nested Text: the web Text is a span and drops `onPress`. */}
          {vote === 'down' ? (
            <Pressable
              onPress={supportPrompt.open}
              className="web:cursor-pointer"
              hitSlop={FOLLOW_UP_HIT_SLOP}
              {...getAccessibilityProps({
                label: 'Tell us what was wrong with this case summary',
                role: 'button',
              })}>
              <Text size="xs" className="font-medium text-typography-900 underline">
                What could we do better?
              </Text>
            </Pressable>
          ) : null}
          {onGoToMessage ? (
            <>
              {vote ? (
                <Text size="xs" className="text-typography-700">
                  ·
                </Text>
              ) : null}
              <Pressable
                onPress={onGoToMessage}
                className="web:cursor-pointer"
                {...getAccessibilityProps({ label: 'Go to the original inquiry', role: 'button' })}>
                <Text size="xs" className="font-medium text-typography-900 underline">
                  Go to message
                </Text>
              </Pressable>
            </>
          ) : null}
        </HStack>
      ) : null}

      {supportPrompt.isFormOpen ? (
        <SupportForm
          isOpen={supportPrompt.isFormOpen}
          onClose={supportPrompt.closeForm}
          npi={currentUser?.npiNumber ?? ''}
          institution={currentUser?.institution ?? ''}
          state={currentUser?.state ?? ''}
          firstName={currentUser?.firstName ?? ''}
          lastName={currentUser?.lastName ?? ''}
          identifiesAsDoctor={currentUser?.identifiesAsDoctor}
          initialValues={{
            reason: SUPPORT_REASON_INQUIRY_SUMMARY_FEEDBACK,
            name: currentUser ? getProfileNameAndInitials(currentUser).name : '',
            email: currentUser?.email ?? '',
            message: `About the case summary on conversation ${conversationId}: `,
          }}
        />
      ) : null}
    </>
  );
}

/** How long a case may read `preparing` before its skeleton row is dropped, in milliseconds. */
export const PREPARING_SKELETON_MAX_MS = 20_000;

const TABULAR_FIGURES_STYLE = { fontVariant: ['tabular-nums' as const] };

/**
 * The recommended layout's case line (see `caseLayoutSwitch`): 14/22 body text with no title and
 * no pill, on the white section the label row above it belongs to. That row says who wrote it and
 * carries the thumbs. Figures are tabular, hyphenation is off, and
 * display-only non-breaking spaces keep "5.3 cm" and "HER2 3+" whole, so the stored line and the
 * screen reader label are the line as written.
 *
 * `preparing` is one skeleton line, dropped after `PREPARING_SKELETON_MAX_MS`. Once it gives up, or
 * the line is blank, it renders nothing.
 */
function RecommendedCaseRow({ clinicalCase }: { clinicalCase: ClinicalCase | undefined }) {
  const isPreparing = clinicalCase?.status === 'preparing';

  const [hasGivenUp, setHasGivenUp] = useState(false);

  useEffect(() => {
    if (!isPreparing) {
      setHasGivenUp(false);

      return undefined;
    }

    const timer = setTimeout(() => setHasGivenUp(true), PREPARING_SKELETON_MAX_MS);

    return () => clearTimeout(timer);
  }, [isPreparing]);

  if (clinicalCase?.status === 'written' && clinicalCase.line.trim()) {
    return (
      <Box
        testID="clinical-case-block"
        accessible
        accessibilityRole="text"
        accessibilityLabel={`Case summary by Primum AI: ${clinicalCase.line}`}>
        <Text
          size="sm"
          className="font-body leading-[22px] text-typography-900 web:max-w-[65ch]"
          style={TABULAR_FIGURES_STYLE}
          {...ANDROID_NO_HYPHENATION}>
          {formatCaseLineForDisplay(clinicalCase.line)}
        </Text>
      </Box>
    );
  }

  if (isPreparing && !hasGivenUp) {
    return (
      <Box
        testID="clinical-case-block"
        accessibilityLiveRegion="polite"
        role="status"
        accessibilityRole="text"
        accessibilityLabel="Preparing case summary"
        className="h-3 w-full rounded-md bg-background-200"
      />
    );
  }

  return null;
}

const ANDROID_NO_HYPHENATION = { android_hyphenationFrequency: 'none' } as const;
